import { NgClass, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { combineLatest } from 'rxjs';
import { ActionsOperators } from '../../data-rules-model';
import { ICustomPositioningConfig } from '../../lib/custom-component-overlay.directive';
import { StaticOptionsProvider } from '../../lib/option-items-providers';
import { PairedAutocompleteComponent } from '../../lib/paired-autocomplete/paired-autocomplete.component';
import { IActionFieldMeta } from '../../meta-data.service';
import { AllowedActions } from '../allowed-actions';
import { BaseActionComponent } from '../base-action.component';

@Component({
  selector: 'app-action-copymoveswap',
  templateUrl: './copy-move-swap.component.html',
  imports: [
    PairedAutocompleteComponent,
    NgSwitch,
    NgClass,
    NgSwitchCase,
    NgSwitchDefault,
    NgIf,
    MatTooltip
  ],
  styleUrls: ['../action.component.scss']
})
export class CopyMoveSwapComponent extends BaseActionComponent implements AfterViewInit {
  filteredToFieldMetaData: IActionFieldMeta[];
  toIsLimited = false;
  infoMessage: string;
  errorMessage: string;
  copySwapMovePlaceholder = 'Choose a field...';

  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  @ViewChild('actionDestinationCopyMoveSwap')
  private fieldPickerComponent: PairedAutocompleteComponent;

  private currentSourceMeta: IActionFieldMeta | null = null;

  ngAfterViewInit() {
    setTimeout(() => {
      combineLatest(
        this.getMeta(this.sourceDescriptorControl.value),
        this.getMeta(this.destinationDescriptorControl.value)
      ).subscribe(([srcFieldMeta, dstFieldMeta]) => {
        const srcField = srcFieldMeta.find(fm => fm.fieldName === this.sourceControl.value);
        const dstField = dstFieldMeta.find(fm => fm.fieldName === this.destinationControl.value);
        this.sourceControl.setValidators(Validators.required);
        this.destinationControl.setValidators(Validators.required);
        this.sourceControlValueChange(srcField as IActionFieldMeta);
        this.destinationControlValueChange(dstField as IActionFieldMeta);
      });
    }, 0);
  }

  protected initialise(): void {
    combineLatest(
      this.getMeta(this.sourceDescriptorControl.value),
      this.getMeta(this.destinationDescriptorControl && this.destinationDescriptorControl.value)
    )
      .subscribe(([filteredSrcMeta, filteredDstMeta]) => {
        const srcField = filteredSrcMeta.find(fm => fm.fieldName === this.sourceControl.value) ?? null;
        const finalMeta = filteredDstMeta
          .filter(fm => this.filterFields(srcField, fm))
          .filter(fm => {
            const srcFieldMeta = filteredSrcMeta.find(sfm => sfm.fieldName === this.sourceControl.value);
            return AllowedActions.actionAllowed(
              ActionsOperators[this.typeControl.value as ActionsOperators],
              srcFieldMeta,
              srcFieldMeta,
              fm
            );
          });
        this.applyNewOptionsProvider(srcField, finalMeta);
        // todo as far as Paired CopyMoveSwap component is Autocomplete (not mat-select) this.filteredToFieldMetaData is not used
        this.filteredToFieldMetaData = finalMeta;
      });
  }

  protected initialiseControls() {
    this.fieldToChangeDescriptorControlValueChangeHandler(this.fieldToChangeDescriptorControl.value);
  }

  protected sourceControlValueChange(selectedField: IActionFieldMeta): void {

    if (!selectedField)
      return;

    this.currentSourceMeta = selectedField;
    this.sourceTypeControl.setValue(selectedField.metaType);

    this.getMeta(this.destinationDescriptorControl.value)
      .subscribe((filteredMeta: IActionFieldMeta[]) => {
        const finalMeta = filteredMeta
          .filter(fm => this.filterFields(selectedField, fm))
          .filter(fm => AllowedActions.actionAllowed(
            ActionsOperators[this.typeControl.value as ActionsOperators],
            selectedField,
            selectedField,
            fm
          ));
        // todo as far as Paired CopyMoveSwap component is Autocomplete (not mat-select) this.filteredToFieldMetaData is not used
        this.filteredToFieldMetaData = finalMeta;
        this.toIsLimited = true;

        this.applyNewOptionsProvider(selectedField, finalMeta);
        const dstField = finalMeta.find(fm => fm.fieldName === this.destinationControl.value);

        if (!this.filterFields(selectedField, dstField)) {
          this.destinationControl.setValue(null);
          this.destinationTypeControl.setValue(null);
        }

        this.buildInfoMessage();
      });
  }

  protected destinationControlValueChangeHandler(fieldName: string): void {
    this.actionDestinationType = this.destinationTypeControl.value;

    this.getMeta(this.destinationDescriptorControl.value)
      .subscribe((filteredMeta: IActionFieldMeta[]) => {
        const meta = filteredMeta
          .filter(fm => this.filterFields(this.currentSourceMeta, fm))
          .find(fm => fm.fieldName === fieldName);
        this.destinationControlValueChange(meta);
      });
  }

  protected destinationControlValueChange(selectedField: IActionFieldMeta): void {

    if (!selectedField)
      return;

    this.destinationTypeControl.setValue(selectedField.metaType);
    this.toIsLimited = false;

    if (!this.filterFields(this.currentSourceMeta, selectedField)) {
      this.sourceControl.setValue(null);
      this.sourceTypeControl.setValue(null);
    }

    this.buildInfoMessage();
  }

  protected destinationDescriptorControlValueChangeHandler(desc: string | null) {

    combineLatest(
      this.getMeta(this.sourceDescriptorControl.value),
      this.getMeta(desc)
    )
      .subscribe(([filteredSrcMeta, filteredDstMeta]) => {
        const srcField = filteredSrcMeta.find(sourceFieldMeta => sourceFieldMeta.fieldName === this.sourceControl.value) ?? null;
        const finalMeta = filteredDstMeta
          .filter(fm => this.filterFields(srcField, fm))
          .filter(fm => {
            const srcFieldMeta = filteredSrcMeta.find(sfm => sfm.fieldName === this.sourceControl.value);
            return AllowedActions.actionAllowed(
              ActionsOperators[this.typeControl.value as ActionsOperators],
              srcFieldMeta,
              srcFieldMeta,
              fm
            );
          });
        const meta = finalMeta.find(fm => fm.fieldName === this.destinationControl.value);
        if (!meta) {
          this.destinationControl.setValue(null);
          this.destinationTypeControl.setValue(null);
        }
        this.applyNewOptionsProvider(srcField, finalMeta);
        this.destinationControlValueChange(meta);
      });
  }

  private applyNewOptionsProvider(sourceMeta: IActionFieldMeta | null, meta: IActionFieldMeta[]) {
    const options = meta
      .filter(fm => this.filterFields(sourceMeta, fm))
      .map(fm => ({name: fm.fieldName, label: fm.label}));
    if (options.length === 0) {
      this.errorMessage = 'There are no supported fields for the selected action.';
    } else {
      this.errorMessage = '';
    }
    this.copySwapMovePlaceholder = this.errorMessage;
    const optionsProvider = new StaticOptionsProvider(options);
    setTimeout(() => { this.fieldPickerComponent.setOptionsProvider(optionsProvider, this.destinationControl.value); }, 0);
  }

  private filterFields(sourceMeta: IActionFieldMeta | null, destinationMeta: IActionFieldMeta): boolean {
    const sourceType = sourceMeta?.metaType ?? '';
    const destinationType = destinationMeta ? destinationMeta.metaType : '';
    if (sourceType === 'TO_ONE') {
      if (destinationType === 'INTEGER' || destinationType === 'STRING') {
        return true;
      }
      if (destinationType === 'TO_ONE' && sourceMeta?.toOneType && destinationMeta?.toOneType) {
        return AllowedActions.isCompatibleToOneCopy(sourceMeta.toOneType, sourceMeta.optionsType, destinationMeta.toOneType);
      }
      return false;
    } else {
      return (sourceType === destinationType || (this.typeControl.value !== 'SWAP' && sourceType === 'INTEGER' && destinationType === 'STRING'))
        && (!destinationMeta || !destinationMeta.readOnly);
    }
  }

  private buildInfoMessage(): void {
    combineLatest(
      this.getMeta(this.sourceDescriptorControl.value),
      this.getMeta(this.destinationDescriptorControl.value)
    ).subscribe(([srcFieldMeta, dstFieldMeta]) => {

      const srcField: IActionFieldMeta = srcFieldMeta.find(fm => fm.fieldName === this.formGroup.controls['source'].value);
      const dstField: IActionFieldMeta = dstFieldMeta
        .filter(fm => this.filterFields(srcField, fm))
        .find(fm => fm.fieldName === this.destinationControl.value);

      if (!srcField || !dstField) {
        this.infoMessage = null;
        return;
      }

      this.infoMessage = '';

      if (this.actionData.type === ActionsOperators.SWAP) {
        if (srcField.maxLength !== dstField.maxLength)
          this.infoMessage = `The field maximum lengths are different. Excess characters will be truncated.`;

        if (CopyMoveSwapComponent.isEncrypted(srcField.fieldName) !== CopyMoveSwapComponent.isEncrypted(dstField.fieldName)) {
          if (this.infoMessage.length > 0)
            this.infoMessage += '\n\n';
          this.infoMessage += 'Your data will no longer be encrypted.';
        }

      } else {
        if (dstField.maxLength < srcField.maxLength)
          this.infoMessage = `This field has a maximum length of ${dstField.maxLength} character(s). Excess characters will be truncated.`;

        if (CopyMoveSwapComponent.isEncrypted(srcField.fieldName) && !CopyMoveSwapComponent.isEncrypted(dstField.fieldName)) {
          if (this.infoMessage.length > 0)
            this.infoMessage += '\n\n';
          this.infoMessage += 'Your data will no longer be encrypted in this field.';
        }
      }

      this.infoMessage = this.infoMessage.length > 0 ? this.infoMessage : null;
    });
  }

  private static isEncrypted(fieldName: string): boolean {
    return fieldName.indexOf('customEncrypt') === 0 || fieldName.indexOf('ssn') === 0;
  }

  copyMoveSwapGridClass(actionType): string {
    let classes: string;
    if (actionType === 'SWAP')
      classes = 'big-input-grid-with';
    if (actionType === 'COPY' || actionType === 'MOVE')
      classes = 'big-input-grid';
    if (this.infoMessage)
      classes += ' big-input-grid-info-icon';
    return classes;
  }
}
